'use client'

import { cva, VariantProps } from 'class-variance-authority'
import { ChangeEvent, ComponentProps, useEffect, useState } from 'react'

import { cn, createSlot } from '@/shared/lib'

export const textareaVariants = cva(
  [
    'typo-label1 block w-full resize-none rounded-sm border border-gray-100 bg-white p-[15px] pr-[7px] transition-colors',
    'placeholder:text-gray-400',
    'scrollbar gutter-stable thin overflow-y-scroll',
  ],
  {
    variants: {
      error: {
        true: 'border-red-500 caret-red-500',
        false: 'focus-visible:border-black focus-visible:outline-none',
      },
      disabled: {
        true: 'cursor-default bg-gray-50 text-gray-400',
        false: '',
      },
      readOnly: {
        true: 'cursor-default bg-gray-50',
        false: '',
      },
    },
  },
)

type TextareaProps = ComponentProps<'textarea'> &
  VariantProps<typeof textareaVariants> & {
    fullWidth?: boolean
    useCount?: boolean
  }

const Textarea = ({
  value: valueProp,
  onChange: onChangeProp,
  readOnly: readOnlyProp = false,
  disabled: disabledProp = false,
  error: errorProp = false,
  fullWidth = false,
  useCount = false,
  maxLength,
  className,
  children,
  ...restProps
}: TextareaProps) => {
  const [value, setValue] = useState<string>((valueProp ?? '').toString())

  const readOnly = readOnlyProp || Boolean(restProps['aria-readonly'])
  const disabled = disabledProp || Boolean(restProps['aria-disabled'])
  const error = errorProp || Boolean(restProps['aria-invalid'])

  const { slots, hasSlot } = createSlot(children, ['message'])

  const shouldRenderHelperText = useCount || hasSlot('message')

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value)
    onChangeProp?.(event)
  }

  useEffect(() => {
    if (valueProp !== value) {
      setValue((valueProp ?? '').toString())
    }
  }, [valueProp])

  return (
    <div className={cn('space-y-2', fullWidth && 'w-full')}>
      <textarea
        data-slot="textarea"
        value={value}
        onChange={handleChange}
        className={cn(
          textareaVariants({ error, disabled, readOnly, className }),
        )}
        maxLength={maxLength}
        readOnly={readOnly}
        disabled={disabled}
        {...restProps}
      />
      {shouldRenderHelperText && (
        <div
          className={cn('flex flex-row-reverse items-center justify-between')}
        >
          {useCount && (
            <span
              className={cn(
                'typo-caption1',
                error ? 'text-red-500' : 'text-gray-500',
              )}
            >
              {value.length ?? 0}{' '}
              <span className={cn(error ? 'text-red-500' : 'text-gray-400')}>
                /
              </span>{' '}
              {maxLength ?? 0}
            </span>
          )}
          {hasSlot('message') && slots.message}
        </div>
      )}
    </div>
  )
}

export { Textarea }
